"use client";

import { ChevronRight, Linkedin, Twitter, Globe } from "lucide-react";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { translateCategoryLabel } from "../../i18n/categories";
import type { ImageFieldValue, LinkTarget } from "../../types";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

export type ArticleItem = {
  category: string;
  title: string;
  excerpt: string;
  author: string;
  authorRole: string;
  date: string;
  href: LinkTarget;
  imageSrc?: ImageFieldValue;
  /** Le chiffre de la fiche, déjà mis en forme en français par le projecteur.
   *  Posé par la bande « Historique récent » des séries à valeur UNIQUE (un taux,
   *  un encours) ; absent partout ailleurs, où la carte ne bouge pas. */
  value?: string;
  /** Unité affichée à côté de `value` (« % », « CDF »). */
  valueUnit?: string;
  /** Les chiffres d'une fiche qui en porte PLUSIEURS (les maturités d'un encours) :
   *  une ligne par valeur sous le titre. Rendus par la carte compacte de la bande. */
  values?: { label: string; value: string; unit?: string }[];
};

/** Article card with category, author, date and share links.
 *  When `imageSrc` is empty (typical for data-record histories — cours-de-change,
 *  secteur-extérieur, etc.), the hero strip + circular avatar are hidden so the
 *  card reads as a title+excerpt+meta tile instead of forcing a generic placeholder
 *  PNG. Editorial articles always carry an image, so the original layout is unchanged
 *  for them. */
export function ArticleCard({ category, title, excerpt, author, authorRole, date, href, imageSrc }: ArticleItem) {
  const locale = useLocale();
  const { src, options } = resolveImageValue(imageSrc);
  const hasImage = Boolean(src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const safeHref = resolveLinkHref(href) ?? "/";
  // Demande client (revue 07/2026) : jamais d'auteur sur le site public.
  void author;
  const hasAuthor = false;
  return (
    <article className="flex h-full flex-col bg-white overflow-hidden rounded-sm">
      {hasImage ? (
        <RuntimeLink href={safeHref} className="block bg-slate-100 rounded-t-sm overflow-hidden">
          <RuntimeImage
            src={src as string}
            alt={t(locale, "articleImageAlt")}
            className={mergeClassNames("h-56 w-full object-cover rounded-t-sm", overrideProps.className)}
            style={mergeStyles(objectPositionStyle, overrideProps.style)}
          />
        </RuntimeLink>
      ) : null}
      <div className="flex flex-1 flex-col bg-[#eaf2fb] rounded-b-sm">
        <div className="flex flex-1 flex-col p-4">
          {/* Tag — compact, small radius, width fits content */}
          {category ? <span className="inline-flex w-fit items-center rounded bg-[#0095DB] px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-white">{translateCategoryLabel(locale, category)}</span> : null}
          <RuntimeLink href={safeHref} className="mt-2 block text-[18px] xl:text-[20px] md:text-[20px] font-semibold leading-6 text-[#005CA9]">
            {title}
          </RuntimeLink>
          {excerpt ? <p className="mt-2 line-clamp-3 text-base xl:text-lg leading-5 text-slate-700">{excerpt}</p> : null}

          {/* Bottom meta block pinned to bottom */}
          <div className="mt-auto pt-4">
            {/* Avatar at left, author and date stacked to the right. Hidden when
                no image exists — the data-record use case has no author either. */}
            <div className="flex items-center gap-3 text-xs xl:text-sm text-slate-600">
              {hasImage && hasAuthor ? (
                <RuntimeImage src={src as string} alt="Auteur" className="h-10 w-10 rounded-full object-cover" />
              ) : null}
              <div className="min-w-0 flex min-h-[40px] flex-col justify-center gap-0.5">
                {hasAuthor ? (
                  <div className="truncate"><span className="font-semibold text-[#005CA9]">{author}</span> <span className="text-slate-500">{authorRole}</span></div>
                ) : null}
                <div className="text-xs xl:text-sm text-slate-600">{date}</div>
              </div>
            </div>
            {/* Media icons bottom-left (editorial articles only); arrow button bottom-right */}
            <div className="mt-2 flex items-center">
              {hasAuthor ? (
                <div className="flex items-center gap-3 text-slate-700">
                  <RuntimeLink href="#" aria-label="LinkedIn"><Linkedin className="h-4 w-4" /></RuntimeLink>
                  <RuntimeLink href="#" aria-label="X"><Twitter className="h-4 w-4" /></RuntimeLink>
                  <RuntimeLink href="#" aria-label="Site"><Globe className="h-4 w-4" /></RuntimeLink>
                </div>
              ) : null}
              <RuntimeLink href={safeHref} aria-label={t(locale, "readArticle")} className="ml-auto grid h-7 w-7 place-items-center rounded-full border border-[#005CA9] text-[#005CA9]">
                <ChevronRight className="h-4 w-4" />
              </RuntimeLink>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}


